import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  TrendingUp,
  IndianRupee,
  Calendar,
  CreditCard,
  PieChart,
  ArrowUpRight,
  Receipt,
  Download
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

export default function RevenuePage({ businessInfo }) {
  const [bookings, setBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchBookings = async () => {
      try {
        const res = await axios.get(`${API}/bookings`);
        setBookings(res.data || []);
      } catch (err) {
        console.error("Error fetching bookings:", err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchBookings();
  }, []);

  // Compute Revenue Stats
  const totalRevenue = bookings.reduce((acc, b) => {
    const total = b.total_amount !== undefined ? b.total_amount : (b.financial_details?.total_amount || b.rent_amount || 0);
    return acc + (Number(total) || 0);
  }, 0);

  const totalAdvance = bookings.reduce((acc, b) => {
    const advance = b.advance_paid !== undefined ? b.advance_paid : (b.financial_details?.advance_paid || b.advance_amount || 0);
    return acc + (Number(advance) || 0);
  }, 0);

  const totalBalanceDue = bookings.reduce((acc, b) => {
    const total = b.total_amount !== undefined ? b.total_amount : (b.financial_details?.total_amount || b.rent_amount || 0);
    const adv = b.advance_paid !== undefined ? b.advance_paid : (b.financial_details?.advance_paid || b.advance_amount || 0);
    const balance = b.balance_due !== undefined ? b.balance_due : (b.financial_details?.balance_due !== undefined ? b.financial_details.balance_due : (total - adv));
    return acc + (Number(balance) || 0);
  }, 0);

  const totalGstCollected = bookings.reduce((acc, b) => {
    const cgst = Number(b.cgst_amount) || Number(b.financial_details?.cgst) || 0;
    const sgst = Number(b.sgst_amount) || Number(b.financial_details?.sgst) || 0;
    return acc + cgst + sgst;
  }, 0);

  return (
    <div className="space-y-6 animate-fade-in max-w-5xl mx-auto" data-testid="revenue-page">
      {/* Top Header with Back Navigation */}
      <div className="flex items-center justify-between">
        <Link
          to="/calendar"
          data-testid="revenue-back-btn"
          className="inline-flex items-center gap-2 text-xs sm:text-sm font-bold text-pink-900 hover:text-pink-950 bg-white px-4 py-2 rounded-xl border border-pink-200 shadow-sm transition-all"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Calendar</span>
        </Link>

        <span className="text-xs font-semibold text-gray-500">
          Financial & Revenue Analytics
        </span>
      </div>

      {/* Main Container Card */}
      <div className="bg-white rounded-3xl border border-pink-100 shadow-xl overflow-hidden">
        {/* Banner */}
        <div className="bg-gradient-to-r from-pink-900 via-pink-800 to-rose-800 p-6 sm:p-8 text-white">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/20 text-xs font-semibold mb-2">
            <TrendingUp className="w-3.5 h-3.5 text-pink-200" />
            <span>Revenue Overview</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight">
            Revenue & Collections Dashboard
          </h1>
          <p className="text-pink-100 text-xs sm:text-sm mt-1 max-w-xl">
            Track total hall rental revenue, advance collections, pending balances, and tax computations across all convention hall events.
          </p>
        </div>

        {/* Content Body */}
        <div className="p-6 sm:p-8 space-y-6 bg-gray-50/30">
          
          {/* Key Metric Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            
            <div className="bg-white p-5 rounded-2xl border border-pink-100 shadow-sm">
              <span className="text-xs font-bold text-gray-500 uppercase tracking-wider block">Total Booked Value</span>
              <div className="text-2xl font-black text-pink-950 mt-1 flex items-center gap-0.5" data-testid="total-revenue-val">
                <IndianRupee className="w-5 h-5 text-pink-700" />
                <span>{totalRevenue.toLocaleString("en-IN")}</span>
              </div>
              <span className="text-[11px] text-gray-400 mt-1 block">{bookings.length} Total Bookings</span>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-emerald-100 shadow-sm">
              <span className="text-xs font-bold text-emerald-700 uppercase tracking-wider block">Total Advances Received</span>
              <div className="text-2xl font-black text-emerald-800 mt-1 flex items-center gap-0.5" data-testid="total-advances-val">
                <IndianRupee className="w-5 h-5 text-emerald-600" />
                <span>{totalAdvance.toLocaleString("en-IN")}</span>
              </div>
              <span className="text-[11px] text-emerald-600 font-semibold mt-1 block">Deposited in Bank</span>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-amber-100 shadow-sm">
              <span className="text-xs font-bold text-amber-700 uppercase tracking-wider block">Pending Balance Due</span>
              <div className="text-2xl font-black text-amber-900 mt-1 flex items-center gap-0.5" data-testid="total-balance-val">
                <IndianRupee className="w-5 h-5 text-amber-600" />
                <span>{totalBalanceDue.toLocaleString("en-IN")}</span>
              </div>
              <span className="text-[11px] text-amber-700 font-semibold mt-1 block">To be collected on event day</span>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-blue-100 shadow-sm">
              <span className="text-xs font-bold text-blue-700 uppercase tracking-wider block">GST Collected (18%)</span>
              <div className="text-2xl font-black text-blue-900 mt-1 flex items-center gap-0.5" data-testid="total-gst-val">
                <IndianRupee className="w-5 h-5 text-blue-600" />
                <span>{totalGstCollected.toLocaleString("en-IN")}</span>
              </div>
              <span className="text-[11px] text-blue-600 font-semibold mt-1 block">CGST (9%) + SGST (9%)</span>
            </div>

          </div>

          {/* Recent Revenue Breakdown Table */}
          <div className="bg-white p-6 rounded-2xl border border-pink-100 shadow-sm space-y-4">
            <div className="flex items-center justify-between border-b border-pink-100 pb-3">
              <h3 className="text-xs font-bold uppercase tracking-wider text-pink-950 flex items-center gap-2">
                <Receipt className="w-4 h-4 text-pink-700" />
                <span>Recent Booking Revenue Breakdown</span>
              </h3>
              <Link
                to="/bookings"
                className="text-xs font-bold text-pink-800 hover:text-pink-950"
              >
                View All Bookings &rarr;
              </Link>
            </div>

            {isLoading ? (
              <div className="text-center py-8 text-xs text-gray-400">Loading revenue records...</div>
            ) : bookings.length === 0 ? (
              <div className="text-center py-8 text-xs text-gray-400">No booking revenue recorded yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="border-b border-gray-100 text-gray-500 font-semibold">
                      <th className="pb-2">Date / Event</th>
                      <th className="pb-2">Customer</th>
                      <th className="pb-2 text-right">Total Amount</th>
                      <th className="pb-2 text-right">Advance Paid</th>
                      <th className="pb-2 text-right">Balance Due</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {bookings.slice(0, 8).map((b) => {
                      const total = Number(b.financial_details?.total_amount || b.rent_amount || 0);
                      const adv = Number(b.financial_details?.advance_paid || b.advance_amount || 0);
                      const bal = b.financial_details?.balance_due !== undefined ? Number(b.financial_details.balance_due) : total - adv;

                      return (
                        <tr key={b.id || b._id} className="hover:bg-pink-50/40 transition-colors">
                          <td className="py-2.5 font-bold text-gray-900">
                            <div>{b.date || "Multi-day"}</div>
                            <div className="text-[10px] text-gray-500 font-normal">{b.event_name || "Wedding Reception"}</div>
                          </td>
                          <td className="py-2.5 text-gray-700 font-medium">{b.customer_name}</td>
                          <td className="py-2.5 text-right font-bold text-pink-950">₹{total.toLocaleString("en-IN")}</td>
                          <td className="py-2.5 text-right font-semibold text-emerald-700">₹{adv.toLocaleString("en-IN")}</td>
                          <td className="py-2.5 text-right font-bold text-amber-800">₹{bal.toLocaleString("en-IN")}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
